import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import ImageCard from "../components/ImageCard";
import ViewToggle, { gridCols } from "../components/ViewToggle";
import useViewPreference from "../hooks/useViewPreference";
import { 
  ArrowLeft, 
  Download, 
  MoreVertical, 
  Check, 
  X, 
  FileText,
  Heart,
  Trash2,
  Upload,
  User,
  ChevronRight,
  ChevronLeft
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CollectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [collection, setCollection] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFilename, setUploadFilename] = useState("");
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadData, setUploadData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [showAssignedPanel, setShowAssignedPanel] = useState(true);
  const { viewMode, setViewMode, imageSize, setImageSize } = useViewPreference("collections");

  const fetchData = async () => {
    try {
      const [collectionRes, imagesRes, usersRes] = await Promise.all([
        axios.get(`${API}/collections/${id}`, { withCredentials: true }),
        axios.get(`${API}/images`, { params: { collection_id: id }, withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      setCollection(collectionRes.data);
      setImages(imagesRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      toast.error("Failed to load collection");
      navigate("/collections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const toggleSelect = (imageId) => {
    setSelectedIds((prev) =>
      prev.includes(imageId) ? prev.filter((i) => i !== imageId) : [...prev, imageId]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === filteredImages.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredImages.map((img) => img.id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    
    try {
      await axios.post(`${API}/images/bulk`, {
        image_ids: selectedIds,
        action
      }, { withCredentials: true });
      toast.success(`Action completed on ${selectedIds.length} images`);
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error("Bulk action failed");
    }
  };

  const handleExport = async () => {
    try {
      const { data } = await axios.get(`${API}/collections/${id}/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${collection.name.replace(/\s+/g, "-")}-export.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Collection exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadPreview(event.target.result);
      setUploadData(event.target.result);
      setUploadFilename(file.name);
      setUploadTitle("");
      setUploadOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData) return;
    
    setUploading(true);
    try {
      await axios.post(`${API}/images/upload`, {
        image_data: uploadData,
        original_filename: uploadFilename,
        title: uploadTitle,
        collection_id: id
      }, { withCredentials: true });
      
      toast.success("Image uploaded");
      setUploadOpen(false);
      setUploadPreview(null);
      setUploadData(null);
      setUploadFilename("");
      setUploadTitle("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchData();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUploadOpenChange = (open) => {
    setUploadOpen(open);
    if (!open) {
      setUploadPreview(null);
      setUploadData(null);
      setUploadFilename("");
      setUploadTitle("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const filteredImages = statusFilter === "all" 
    ? images 
    : images.filter((img) => img.status === statusFilter);

  // Group images by assigned user
  const getAssignedImages = (userId) => {
    return images.filter(img => img.assigned_to === userId);
  };

  const unassignedImages = images.filter(img => !img.assigned_to);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  if (!collection) return null;

  return (
    <div className="pb-20 md:pb-0" data-testid="collection-detail">
      <div className="flex gap-6">
        {/* Main Content */}
        <div className={`flex-1 ${showAssignedPanel ? 'lg:pr-0' : ''}`}>
          {/* Header */}
          <div className="flex items-start gap-4 mb-6">
            <Link 
              to="/collections" 
              className="p-2 border border-gray-200 hover:border-black transition-colors"
              data-testid="back-to-collections"
            >
              <ArrowLeft size={20} />
            </Link>
        <div className="flex-1">
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            {collection.name}
          </h1>
          {collection.description && (
            <p className="text-gray-500 text-sm mt-1">{collection.description}</p>
          )}
          <p className="text-xs uppercase tracking-widest text-gray-400 mt-2">
            {collection.image_count} images
          </p>
        </div>
        <Button
          onClick={handleExport}
          variant="outline"
          className="rounded-none hidden md:flex"
          data-testid="export-collection"
        >
          <Download size={16} className="mr-2" />
          Export
        </Button>
        
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          className="hidden"
        />
        
        <Button
          onClick={() => fileInputRef.current?.click()}
          className="rounded-none bg-black text-white hover:bg-gray-800"
          data-testid="upload-image-btn"
        >
          <Upload size={16} className="mr-2" />
          Upload Image
        </Button>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 p-4 border border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selectedIds.length === filteredImages.length && filteredImages.length > 0}
              onCheckedChange={selectAll}
              data-testid="select-all"
            />
            <span className="text-sm text-gray-500">
              {selectedIds.length > 0 ? `${selectedIds.length} selected` : "Select all"}
            </span>
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 rounded-none border-gray-300" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="affinity">Affinity</SelectItem>
              <SelectItem value="print-ready">Print Ready</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <ViewToggle viewMode={viewMode} setViewMode={setViewMode} imageSize={imageSize} setImageSize={setImageSize} />

        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-none" data-testid="bulk-actions">
                  <MoreVertical size={16} className="mr-2" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleBulkAction("approve")} data-testid="bulk-approve">
                  <Check size={14} className="mr-2 text-green-600" />
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkAction("reject")} data-testid="bulk-reject">
                  <X size={14} className="mr-2 text-red-600" />
                  Reject
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkAction("draft")} data-testid="bulk-draft">
                  <FileText size={14} className="mr-2 text-yellow-600" />
                  Set Draft
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkAction("favorite")} data-testid="bulk-favorite">
                  <Heart size={14} className="mr-2 text-pink-600" />
                  Favorite
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkAction("delete")} className="text-red-600" data-testid="bulk-delete">
                  <Trash2 size={14} className="mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Images Grid */}
      {filteredImages.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-8 text-center">
          <p className="text-gray-500 text-sm">
            {statusFilter !== "all" ? "No images with this status" : "No images in this collection"}
          </p>
          <Link 
            to="/generate" 
            className="text-sm text-black underline mt-2 inline-block"
          >
            Generate images
          </Link>
        </div>
      ) : viewMode === "list" ? (
        <div className="flex flex-col divide-y divide-gray-100 border border-gray-200">
          {filteredImages.map((image) => (
            <div key={image.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
              <Checkbox
                checked={selectedIds.includes(image.id)}
                onCheckedChange={() => toggleSelect(image.id)}
              />
              <img
                src={image.image_data}
                alt={image.title || image.prompt}
                className="w-12 h-12 object-cover flex-shrink-0 border border-gray-200"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{image.title || image.prompt || "Untitled"}</p>
                <span className={`text-[10px] uppercase tracking-wider ${
                  image.status === 'approved' ? 'text-green-600' :
                  image.status === 'rejected' ? 'text-red-600' :
                  image.status === 'print-ready' ? 'text-blue-600' :
                  image.status === 'affinity' ? 'text-purple-600' : 'text-yellow-600'
                }`}>{image.status}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`grid ${gridCols(imageSize)} gap-4`}>
          {filteredImages.map((image) => (
            <ImageCard
              key={image.id}
              image={image}
              selected={selectedIds.includes(image.id)}
              onSelect={() => toggleSelect(image.id)}
              onUpdate={fetchData}
            />
          ))}
        </div>
      )}
      </div>

      {/* Assigned Tasks Panel */}
      <div className={`hidden lg:block transition-all ${showAssignedPanel ? 'w-72' : 'w-0'}`}>
        {showAssignedPanel && (
          <div className="border border-gray-200 bg-white sticky top-4">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-heading font-bold text-sm uppercase tracking-widest">Assigned Tasks</h3>
              <button 
                onClick={() => setShowAssignedPanel(false)}
                className="p-1 hover:bg-gray-100"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            
            <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
              {users.map((user) => {
                const userImages = getAssignedImages(user.id);
                return (
                  <div key={user.id} className="border-b border-gray-100">
                    <div className="p-3 bg-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-black text-white flex items-center justify-center text-xs font-bold">
                          {user.name.charAt(0)}
                        </div>
                        <span className="font-medium text-sm">{user.name}</span>
                      </div>
                      <span className="text-xs bg-gray-200 px-2 py-0.5">{userImages.length}</span>
                    </div>
                    
                    {userImages.length > 0 ? (
                      <div className="p-2 space-y-2">
                        {userImages.map((img) => (
                          <div key={img.id} className="flex items-center gap-2 p-2 border border-gray-100 hover:border-gray-300 transition-colors">
                            <img 
                              src={img.image_data}
                              alt={img.prompt}
                              className="w-10 h-10 object-cover flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-700 truncate">{img.prompt}</p>
                              <span className={`text-[10px] uppercase px-1 ${
                                img.status === 'approved' ? 'text-green-600' :
                                img.status === 'rejected' ? 'text-red-600' : 'text-yellow-600'
                              }`}>
                                {img.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="p-3 text-xs text-gray-400">No tasks assigned</p>
                    )}
                  </div>
                );
              })}
              
              {/* Unassigned */}
              <div>
                <div className="p-3 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gray-400 text-white flex items-center justify-center text-xs">
                      ?
                    </div>
                    <span className="font-medium text-sm text-gray-500">Unassigned</span>
                  </div>
                  <span className="text-xs bg-gray-200 px-2 py-0.5">{unassignedImages.length}</span>
                </div>
                {unassignedImages.length > 0 && (
                  <div className="p-2 space-y-2">
                    {unassignedImages.slice(0, 5).map((img) => (
                      <div key={img.id} className="flex items-center gap-2 p-2 border border-gray-100 hover:border-gray-300 transition-colors">
                        <img 
                          src={img.image_data}
                          alt={img.prompt}
                          className="w-10 h-10 object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-700 truncate">{img.prompt}</p>
                          <span className={`text-[10px] uppercase px-1 ${
                            img.status === 'approved' ? 'text-green-600' :
                            img.status === 'rejected' ? 'text-red-600' : 'text-yellow-600'
                          }`}>
                            {img.status}
                          </span>
                        </div>
                      </div>
                    ))}
                    {unassignedImages.length > 5 && (
                      <p className="text-xs text-gray-400 text-center py-1">+{unassignedImages.length - 5} more</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Toggle button when panel is hidden */}
      {!showAssignedPanel && (
        <button
          onClick={() => setShowAssignedPanel(true)}
          className="hidden lg:flex fixed right-4 top-1/2 -translate-y-1/2 items-center gap-1 px-2 py-4 bg-black text-white text-xs uppercase tracking-widest writing-mode-vertical"
          style={{ writingMode: 'vertical-rl' }}
          data-testid="show-assigned-panel"
        >
          <ChevronLeft size={14} />
          Assigned
        </button>
      )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={handleUploadOpenChange}>
        <DialogContent className="rounded-none bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Upload Image</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            {uploadPreview && (
              <div className="border border-gray-200 aspect-square bg-gray-50">
                <img
                  src={uploadPreview}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Original Filename</Label>
              <Input
                value={uploadFilename}
                readOnly
                className="rounded-none border-gray-300 bg-gray-100 text-gray-600"
                data-testid="upload-original-filename"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Title (Optional)</Label>
              <Input
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Add a custom title..."
                className="rounded-none border-gray-300 bg-white text-black"
                autoFocus
                data-testid="upload-image-title"
              />
            </div>
            <Button 
              type="submit" 
              disabled={uploading || !uploadData}
              className="w-full rounded-none bg-black text-white hover:bg-gray-800"
              data-testid="upload-image-submit"
            >
              {uploading ? "Uploading..." : "Upload to Collection"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
